import {
  BILLING_PLANS,
  DEFAULT_CURRENCY,
  SUPPORTED_CURRENCIES,
  savingsPercent,
  type Currency,
} from './billing.js';

/**
 * What the landing page shows when the live prices cannot be read.
 *
 * Money is read from Whop at display time (see billing.ts), so this table is
 * the one deliberate exception: a copy, kept next to the credits it is priced
 * against, used only when the public-plans endpoint is unreachable or answers
 * with something the page does not recognise. A wrong price is worse than a
 * stale one, and a blank card is worse than both, so the page starts from
 * these and replaces them only with an answer it has validated.
 *
 * There is one table per currency, all built from the list prices in
 * `BILLING_PLANS`, so a visitor shown lira by the server is never handed a
 * dollar fallback when the next request fails.
 *
 * Amounts are minor units. A yearly price is about 20% under twelve monthly
 * ones; the credits still arrive monthly, so `credits` is the monthly
 * allowance on either interval.
 */

export type PublicPrice = { amount: number; currency: string };

export type PublicPlan = {
  id: string;
  name: string;
  /** The monthly allowance; a yearly plan grants it every month, not up front. */
  credits: number;
  monthly: PublicPrice;
  yearly: PublicPrice | null;
  savingsPercent: number | null;
  /** Stores that may be connected at once on this plan; null is unlimited. */
  storeLimit: number | null;
  /** False for a plan arranged by agreement: its card offers a conversation, not checkout. */
  selfServe: boolean;
};

function fallbackIn(currency: Currency): readonly PublicPlan[] {
  return BILLING_PLANS.map((plan) => {
    const price = plan.prices[currency];
    return {
      id: plan.id,
      name: plan.name,
      credits: plan.credits,
      monthly: { amount: price.month, currency },
      // One source: the amount the Whop script creates the yearly plan at. A
      // plan with no year in this currency has none, and so no saving to
      // speak of; the saving is worked out per currency, because a euro year
      // rounded to a whole price is not the same percentage as a dollar one.
      yearly: price.year === null ? null : { amount: price.year, currency },
      savingsPercent: price.year === null ? null : savingsPercent(price.month, price.year),
      storeLimit: plan.storeLimit,
      selfServe: plan.selfServe,
    };
  });
}

/** The fallback prices in every currency the plans are sold in. */
export const PUBLIC_PRICING_FALLBACKS: Readonly<Record<Currency, readonly PublicPlan[]>> =
  Object.fromEntries(
    SUPPORTED_CURRENCIES.map((currency) => [currency, fallbackIn(currency)]),
  ) as Record<Currency, readonly PublicPlan[]>;

/** The dollar table, which is what the page starts from before it knows the visitor's currency. */
export const PUBLIC_PRICING_FALLBACK: readonly PublicPlan[] =
  PUBLIC_PRICING_FALLBACKS[DEFAULT_CURRENCY];

export function publicPricingFallback(currency: Currency): readonly PublicPlan[] {
  return PUBLIC_PRICING_FALLBACKS[currency];
}
